import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/live";
import { fmtDate, rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { MoneyInput } from "@/components/MoneyInput";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader } from "@/components/common";
import { InvoiceStatus } from "@/components/InvoiceStatus";

const CfgForm = ({ w, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (w) { const b = w.billing || {}; setF({ fee: String(b.fee_per_resort ?? ""), day: String(b.bill_day ?? 1), due: String(b.due_days ?? 7), enabled: b.enabled ?? false }); }
  }, [w]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/billing/wilayah/${w.id}`, { fee_per_resort: Number(f.fee) || 0, bill_day: Number(f.day), due_days: Number(f.due), enabled: f.enabled });
      toast.success("Pengaturan tagihan disimpan"); onClose(); onSaved();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const num = (k, max) => (e) => setF({ ...f, [k]: String(Math.min(max, Number(e.target.value.replace(/\D/g, "") || 0))) });
  return (
    <Dialog open={Boolean(w)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="billing-cfg-dialog">
        <DialogHeader><DialogTitle className="font-heading">Tagihan {w?.name}</DialogTitle><DialogDescription>{w?.resort_count} resort aktif dihitung tiap bulan.</DialogDescription></DialogHeader>
        {f && <form onSubmit={submit} className="space-y-4">
          <Field label="Biaya Bulanan per Resort"><MoneyInput value={f.fee} onChange={(v) => setF({ ...f, fee: v })} className="h-11" testId="billing-fee-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal Tagihan (1-28)"><Input inputMode="numeric" className="h-11" value={f.day} onChange={num("day", 28)} data-testid="billing-day-input" /></Field>
            <Field label="Jatuh Tempo (hari)"><Input inputMode="numeric" className="h-11" value={f.due} onChange={num("due", 60)} data-testid="billing-due-input" /></Field>
          </div>
          <div className="rounded-2xl bg-muted/50 p-4 flex justify-between text-sm"><span>Total tagihan / bulan</span><b className="font-mono text-primary" data-testid="billing-total-preview">{rupiah((Number(f.fee) || 0) * (w?.resort_count || 0))}</b></div>
          <label className="flex items-center justify-between rounded-2xl border p-4">
            <span><span className="block font-medium">Tagihan aktif</span><span className="block text-xs text-muted-foreground">Tagihan dibuat otomatis setiap bulan</span></span>
            <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="billing-enabled-toggle" />
          </label>
          <DialogFooter><Button type="submit" className="rounded-full h-11 px-6" data-testid="billing-cfg-submit">Simpan</Button></DialogFooter>
        </form>}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ w, onEdit }) => (
  <tr className="border-b last:border-0" data-testid={`billing-row-${w.id}`}>
    <td className="py-3 pl-5 font-medium">{w.name}{!w.active && <span className="ml-2 text-[11px] text-rose-600">Nonaktif</span>}</td>
    <td className="py-3 px-3 text-right font-mono">{w.cabang_count}</td>
    <td className="py-3 px-3 text-right font-mono">{w.resort_count}</td>
    <td className="py-3 px-3 whitespace-nowrap text-sm">{w.created_at ? fmtDate(w.created_at) : "-"}</td>
    <td className="py-3 px-3 text-right font-mono">{w.billing?.enabled ? rupiah(w.billing.fee_per_resort * w.resort_count) : <span className="text-muted-foreground text-xs">Tidak aktif</span>}</td>
    <td className="py-3 px-3">{w.last_invoice ? <InvoiceStatus s={w.last_invoice.status} overdue={w.overdue} /> : "-"}{w.pending_proof > 0 && <span className="ml-1 text-[11px] font-semibold text-amber-600" data-testid={`billing-pending-${w.id}`}>{w.pending_proof} bukti baru</span>}</td>
    <td className="py-3 pr-5 text-right whitespace-nowrap">
      <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full mr-1" onClick={() => onEdit(w)} aria-label="Edit" data-testid={`billing-edit-${w.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      <Button asChild size="sm" variant="outline" className="h-8 rounded-full"><Link to={`/tagihan-layanan/${w.id}`} data-testid={`billing-detail-${w.id}`}>Rincian</Link></Button>
    </td>
  </tr>
);

export default function TagihanLayananPage() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get("/billing/wilayah").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); }); }, []);
  useEffect(() => { load(); }, [load]);
  usePoll(load, 15000);
  const list = (items || []).filter((w) => w.active || w.billing?.enabled);
  return (
    <div>
      <PageHeader title="Tagihan Layanan" />
      {items === null ? <p className="text-muted-foreground">Memuat...</p> : list.length === 0 ? (
        <EmptyState icon={Receipt} title="Belum ada kantor wilayah" text="Kantor wilayah aktif akan tampil di sini." testId="billing-empty" />
      ) : (
        <div className="panel !p-0 overflow-x-auto" data-testid="billing-table">
          <table className="w-full text-sm min-w-[860px]">
            <thead><tr className="text-left text-xs text-muted-foreground border-b">
              <th className="py-3 pl-5 font-medium">Nama Wilayah</th><th className="py-3 px-3 font-medium text-right">Kantor Cabang</th><th className="py-3 px-3 font-medium text-right">Resort</th>
              <th className="py-3 px-3 font-medium">Tanggal Dibuat</th><th className="py-3 px-3 font-medium text-right">Tagihan / Bulan</th><th className="py-3 px-3 font-medium">Status Terakhir</th><th className="py-3 pr-5" />
            </tr></thead>
            <tbody>{list.map((w) => <Row key={w.id} w={w} onEdit={setEdit} />)}</tbody>
          </table>
        </div>
      )}
      <CfgForm w={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
